/**
 * For now, chat is the only AI feature that is surfaced through the sidepanel, so this file lives in
 * the llm/chat-library folder.
 */
import {StyleSheet, css} from 'aphrodite';
import classNames from 'classnames';
import React, {useCallback, useRef} from 'react';
import {v4 as uuidv4} from 'uuid';

import {ChatType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {CrumbLink, SplitBreadcrumbs} from 'nav/Breadcrumbs';
import ListingHeader from 'nav/sidepanel/header/ListingHeader';
import * as SidepanelActions from 'nav/sidepanel/SidepanelActions';
import ToggleDropdown from 'nav/ToggleDropdown';
import {ButtonSize} from 'ui-library/buttons/Button';
import {GRAY_LIGHT, PURPLE_DARK} from 'ui-library/Colors';
import makeUseGlobalClientState from 'ui-library/hooks/global-client-state/makeUseGlobalClientState';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {assertNever} from 'util/Types';

import {ChatCrumbLink, NewChatCrumbLink} from './ChatCrumbLinks';
import ChatListing from './ChatListing';
import ChatRouter, {ChatRequest} from './ChatRouter';
import {isChatTypeEnabled} from './ChatTypeUtil';
import {NewChatDropdown} from './NewChatDropdown';

export interface AISidepanelProps {
  isExpanded: boolean;
}

export enum ActivePanel {
  CHAT = 'CHAT',
  HISTORY = 'HISTORY',
}

export interface AIPanelState {
  /**
   * A client-local ID used as the key for the ChatRouter component. This allows us to ensure that
   * state does not leak between chat sessions when rendering ChatRouter. This is simply not the chat
   * ID since that does not exist when a new chat is started.
   */
  clientChatId: string;
  /**
   * When the sidepanel is collapsed, which panel should be active. This should reflect the panel
   * the user last interacted with, e.g. double toggling should be a no-op.
   */
  activeCollapsedPanel: ActivePanel;
  /**
   * The chat session that is currently active.
   */
  activeChat: ChatRequest;
}

export function _getDefaultChatType(): ChatType {
  return Object.values(ChatType).find((chatType) => isChatTypeEnabled(chatType)) ?? ChatType.DEEP_RESEARCH;
}

export const panelStateHook = makeUseGlobalClientState<AIPanelState>({
  activeCollapsedPanel: ActivePanel.CHAT,
  activeChat: {
    id: null,
    // TODO: Defaulting to the first available type is unintuitive. We may either wish to use `null`
    // to signal a generic chat entrypoint, or perhaps introduce a ChatType.GENERIC_ASSISTANT that is
    // capabable of routing the user to one of the other specific agent types.
    type: _getDefaultChatType(),
  },
  clientChatId: uuidv4(),
});

export function AISidepanel(props: AISidepanelProps): JSX.Element {
  const [panelState, setPanelState] = panelStateHook.useGlobalClientState('ai-panel-state');
  const refetchRef = useRef<() => void>(() => {});

  const isChatActive = props.isExpanded || panelState.activeCollapsedPanel === ActivePanel.CHAT;
  const isNewChatActive = panelState.activeChat.id == null && isChatActive;

  const chatListing = (
    <div className={classNames(css(styles.chatListingContainer), 'scrollbar-override')}>
      <ChatListing
        selectedChatSession={
          isNewChatActive || panelState.activeChat.id != null ? panelState.activeChat : null
        }
        onSelectChatSession={(chat: ChatRequest) => {
          setPanelState({
            ...panelState,
            clientChatId: panelState.activeChat.id === chat.id ? panelState.clientChatId : uuidv4(),
            activeChat: chat,
            // Even if we are expanded, the last thing the user interacted was the chat, so let's
            // keep the chat open when collapsing.
            activeCollapsedPanel: ActivePanel.CHAT,
          });
        }}
        refetchRef={refetchRef}
      />
    </div>
  );

  // NOTE: It's a bit sketchy that our callback is dependent on the current panelState. There is some risk
  // that the component will invoke the callback asynchronously where it will invoke the stale callback.
  // React's setState avoids this by having a callback API: `setState((oldState) => )`, which
  // useGlobalClientState does not support. This will be a bigger issue if there is intentionally other
  // state that the user accumulates while waiting for the chat to be created. In practice, it may not
  // be an issue since creating a chat on the backend is a relatively quick operation.
  const onCreateChat = useCallback(
    (chat: ChatRequest): void => {
      setPanelState({...panelState, activeChat: chat});
      refetchRef.current();
    },
    [setPanelState, panelState, refetchRef]
  );

  const onRequestNewChat = useCallback(
    (newChatType: ChatType): void => {
      setPanelState({...panelState, activeChat: {id: null, type: newChatType}, clientChatId: uuidv4()});
      refetchRef.current();
    },
    [setPanelState, panelState, refetchRef]
  );

  const chat = (
    <div className={classNames(css(styles.chatWrapper), 'scrollbar-override')}>
      <ChatRouter
        key={panelState.clientChatId}
        chat={panelState.activeChat}
        onCreateChat={onCreateChat}
        onRequestNewChat={onRequestNewChat}
      />
    </div>
  );
  return (
    <div className={classNames('sidepanel', css(styles.sidepanel))}>
      <div
        className={classNames(css(styles.sidepanelHeader), {
          [css(styles.sidepanelHeaderExpanded)]: props.isExpanded,
        })}
      >
        <ListingHeader
          className={css(styles.listingHeader)}
          isExpanded={props.isExpanded}
          crumbs={
            <div className="u-flex-grow u-min-width-zero">
              <SplitBreadcrumbs
                crumbs={[
                  <CrumbLink
                    key="ai-chats"
                    text="AI Chats"
                    icon={<BenchlingIcon icon="sparkle" color={PURPLE_DARK} />}
                    onClick={() => {
                      setPanelState({...panelState, activeCollapsedPanel: ActivePanel.HISTORY});
                      SidepanelActions.unexpand();
                    }}
                  />,
                  ...(props.isExpanded || panelState.activeCollapsedPanel === ActivePanel.CHAT
                    ? [
                        panelState.activeChat.id ? (
                          <ChatCrumbLink key="chat" chatSessionId={panelState.activeChat.id} />
                        ) : (
                          <NewChatCrumbLink key="new-chat" />
                        ),
                      ]
                    : []),
                ]}
              />
            </div>
          }
          buttons={
            <div className={css(styles.buttonsContainer)}>
              <ToggleDropdown expanded={props.isExpanded} />
              <NewChatDropdown
                dropLeft={props.isExpanded}
                buttonSize={ButtonSize.SMALL}
                activeNewChatType={isNewChatActive ? panelState.activeChat.type : null}
                onClick={(chatType) => {
                  setPanelState({
                    ...panelState,
                    activeChat: {id: null, type: chatType},
                    clientChatId: uuidv4(),
                    activeCollapsedPanel: props.isExpanded
                      ? panelState.activeCollapsedPanel
                      : ActivePanel.CHAT,
                  });
                }}
              />
            </div>
          }
          showShadow={true}
        />
      </div>
      {
        // Handle CHAT cases with one JSX tree to ensure that expanding / collapsing the sidepanel does not
        // cause the chat to unmount / remount... thus avoiding losing input state.
        isChatActive ? (
          <div className={css(styles.container)}>
            {props.isExpanded && <div className={css(styles.expandedChatHistoryList)}>{chatListing}</div>}
            {chat}
          </div>
        ) : (
          (() => {
            switch (panelState.activeCollapsedPanel) {
              case ActivePanel.CHAT:
                throw new Error('Expected isChatActive to be true');
              case ActivePanel.HISTORY:
                return chatListing;
              default:
                throw new Error(
                  `Unexpected active collapsed panel: ${assertNever(panelState.activeCollapsedPanel)}`
                );
            }
          })()
        )
      }
    </div>
  );
}

const styles = StyleSheet.create({
  chatListingContainer: {
    overflowY: 'scroll',
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
  },
  sidepanel: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  sidepanelHeader: {
    padding: 0,
    display: 'flex',
  },
  sidepanelHeaderExpanded: {
    padding: '16px 12px 0',
    borderBottom: `1px solid ${GRAY_LIGHT}`,
  },
  listingHeader: {
    flex: 1,
    minWidth: 0,
  },
  container: {
    display: 'flex',
    flexDirection: 'row',
    flexGrow: 1,
    minHeight: 0,
  },
  chatWrapper: {
    padding: '8px',
    display: 'flex',
    flex: 1,
    overflowY: 'scroll',
  },
  breadcrumbsContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  buttonsContainer: {
    display: 'flex',
    gap: '8px',
    marginLeft: '8px',
  },
  expandedChatHistoryList: {
    width: '312px', // TODO: see how this is calculated for normal collapsed sidepanel.
    flex: 'none',
    borderRight: `1px solid ${GRAY_LIGHT}`,
    display: 'flex',
    flexDirection: 'column',
  },
});

export default AISidepanel;
